/* Interface publique française - Version production */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/Header';
import { SEO } from '@/components/SEO';
import { Footer } from '@/components/Footer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const TRAVELER_IDS = [
  'whatIsRidalo',
  'howToBook',
  'bookingConfirmed',
  'cancelBooking',
  'providerCancels',
  'contactProvider',
];

const PROVIDER_IDS = [
  'becomeProvider',
  'manageServices',
  'manageAvailability',
  'whatIsExternal',
  'externalCommission',
  'commissions',
];

// Mapping camelCase → kebab-case for data-testid stability (identical to pre-i18n)
const TESTID_MAP = {
  whatIsRidalo: 'what-is-ridalo',
  howToBook: 'how-to-book',
  bookingConfirmed: 'booking-confirmed',
  cancelBooking: 'cancel-booking',
  providerCancels: 'provider-cancels',
  contactProvider: 'contact-provider',
  becomeProvider: 'become-provider',
  manageServices: 'manage-services',
  manageAvailability: 'manage-availability',
  whatIsExternal: 'what-is-external',
  externalCommission: 'external-commission',
  commissions: 'commissions',
};

const FaqGroup = ({ title, ids, group, testidPrefix, t }) => (
  <section className="mt-10" data-testid={`${testidPrefix}-section`}>
    <h2 className="text-2xl sm:text-3xl font-semibold text-primary mb-4">{title}</h2>
    <Accordion type="single" collapsible className="w-full">
      {ids.map((id) => (
        <AccordionItem key={id} value={id} data-testid={`faq-item-${TESTID_MAP[id]}`}>
          <AccordionTrigger className="text-left text-base font-medium text-primary hover:no-underline">
            {t(`faq.${group}.${id}Q`)}
          </AccordionTrigger>
          <AccordionContent className="text-slate-600 leading-relaxed text-sm sm:text-base">
            {t(`faq.${group}.${id}A`)}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export default function FAQPage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title={t('seo.faq.title')}
        description={t('seo.faq.description')}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            ...TRAVELER_IDS.map((id) => ['traveler', id]),
            ...PROVIDER_IDS.map((id) => ['provider', id]),
          ].map(([group, id]) => ({
            '@type': 'Question',
            name: t(`faq.${group}.${id}Q`),
            acceptedAnswer: { '@type': 'Answer', text: t(`faq.${group}.${id}A`) },
          })),
        }}
      />
      <Header />
      <main className="flex-1 py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="faq-page">
          <h1 className="text-4xl sm:text-5xl font-bold text-primary tracking-tight">
            {t('faq.pageTitle')}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
            {t('faq.pageIntro')}
          </p>

          <FaqGroup
            title={t('faq.travelersSectionTitle')}
            ids={TRAVELER_IDS}
            group="traveler"
            testidPrefix="faq-travelers"
            t={t}
          />
          <FaqGroup
            title={t('faq.providersSectionTitle')}
            ids={PROVIDER_IDS}
            group="provider"
            testidPrefix="faq-providers"
            t={t}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
